// ---------------------------------------------------------------------------
// The beat before a round starts.
//
// It must not read as a loading screen, so it says what is about to happen
// rather than that something is happening: which round, of how many, of what
// type, and who is choosing.
//
// There is deliberately no countdown here, and the reason is the length rather
// than the mechanism. This screen holds for `ROUND_INTRO_MS`, two and a half
// seconds — a clock would be gone before anybody had read it, and a disc that
// appears and vanishes inside one breath reads as a glitch rather than a wait.
//
// Not because a countdown here would drift: `useClock` renders the server's own
// deadline every frame, cannot drift, and freezes when the server does. Every
// other timer in the game is one of those. If this beat ever grows long enough
// to be worth waiting out, it should have one too.
//
// The block that names the chooser has a floor under it. It arrives as "the
// board is being drawn" and becomes a name a moment later, and without the floor
// that swap is the title above it jumping half a line up the screen.
// ---------------------------------------------------------------------------

import { PlayerChip, RoundTypeChip, StandingsStrip } from '../../components/primitives/index.js';
import { displayColorOf, playerById } from '../../lib/playerLookup.js';
import { roundLabel } from '../../lib/format.js';
import type { RoundType } from '@countdown/shared';
import type { RoomScreenProps } from './types.js';

/**
 * What the chooser is about to do, in the round's own terms. Two forms, because
 * the same sentence is addressed to the chooser and about them: one verb list
 * gave "You picks the nine letters".
 */
const CHOOSER_TASK: Record<RoundType, { you: string; they: string }> = {
  letters: { you: 'pick the nine letters', they: 'picks the nine letters' },
  numbers: {
    you: 'pick how many large numbers',
    they: 'picks how many large numbers',
  },
  conundrum: { you: 'have nothing to pick', they: 'has nothing to pick' },
};

export function RoundIntroScreen({ state }: RoomScreenProps) {
  const round = state.rounds[state.currentRoundIndex];
  const type = round?.type ?? state.settings.roundSequence[state.currentRoundIndex] ?? 'letters';
  const chooser = playerById(state, round?.chooserId ?? null);
  const youAreChooser = chooser !== null && chooser.id === state.youId;
  const isLast = state.currentRoundIndex === state.totalRounds - 1;

  return (
    <div className="mx-auto flex w-full max-w-[720px] flex-col gap-6 px-4 py-6 sm:max-w-[860px]">
      <StandingsStrip state={state} showRoundCounter />

      <section className="fade-in flex flex-col items-center gap-5 rounded-frame border border-line bg-bg-alt px-4 py-10 text-center sm:py-14">
        <span className="label-mono">
          round {state.currentRoundIndex + 1} of {state.totalRounds}
          {isLast ? ' · last one' : ''}
        </span>

        <div className="flex items-center gap-4">
          <RoundTypeChip type={type} size="md" />
          <h1
            className="text-[44px] leading-none font-extrabold sm:text-[62px]"
            style={{ letterSpacing: '-0.03em' }}
          >
            {roundLabel(type)}
          </h1>
        </div>

        <div className="flex min-h-[104px] flex-col items-center justify-center">
          {type === 'conundrum' ? (
            <p className="max-w-[42ch] text-ink-muted">
              Nine letters, one nine-letter word. First to buzz gets ten seconds alone.
            </p>
          ) : chooser === null ? (
            <p className="max-w-[42ch] text-ink-muted">The board is being drawn.</p>
          ) : youAreChooser ? (
            <div className="flex flex-col items-center gap-2">
              <span className="label-mono text-accent">your call</span>
              <p className="max-w-[42ch] text-ink-muted">You {CHOOSER_TASK[type].you}.</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <span className="label-mono">choosing</span>
              <PlayerChip player={chooser} color={displayColorOf(state, chooser)} size="lg" />
              <p className="max-w-[42ch] text-ink-muted">
                {chooser.displayName} {CHOOSER_TASK[type].they}.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
